import {
  CapacitorSQLite,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';

const SQLITE_DB_NAME = 'watchduty-store.db';
const KEYVAL_TABLE = 'watch_duty_keyval';
const DB_VERSION = 1;

let dbInstance: SQLiteDBConnection | null = null;

const attemptStringify = <T>(input: T): string => {
  try {
    return JSON.stringify(input);
  } catch (e) {
    return String(input);
  }
};

export const resetDb = (): void => {
  dbInstance = null;
};

export const getDatabase = async (): Promise<SQLiteDBConnection> => {
  if (dbInstance) {
    const isOpen = await dbInstance.isDBOpen();
    if (isOpen) {
      return dbInstance;
    }
  }

  const sqliteConnection = new SQLiteConnection(CapacitorSQLite);

  try {
    const retCC = (await sqliteConnection.checkConnectionsConsistency()).result;
    const isConn = (await sqliteConnection.isConnection(SQLITE_DB_NAME, false))
      .result;

    if (retCC && isConn) {
      const db = await sqliteConnection.retrieveConnection(
        SQLITE_DB_NAME,
        false,
      );
      await db.open();
      await db.isDBOpen();
      return db;
    }

    dbInstance = await sqliteConnection.createConnection(
      SQLITE_DB_NAME,
      false, // encrypted?
      'no-encryption', // mode
      DB_VERSION,
      false, // read-only?
    );

    await dbInstance.open();
    await dbInstance.isDBOpen();
    return dbInstance;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const msg = error.message ? error.message : error;
    throw new Error(`Failed to open sqlite database: ${msg}`);
  }
};

export const createSQLiteTable = async (): Promise<void> => {
  const db = await getDatabase();

  await db.query(
    `CREATE TABLE IF NOT EXISTS ${KEYVAL_TABLE} (key TEXT PRIMARY KEY, value TEXT);`,
  );
};

export const setSQLiteItem = async <T>(
  key: string,
  value: T,
): Promise<void> => {
  const db = await getDatabase();
  const valueString = attemptStringify(value);

  await db.query(
    `INSERT OR REPLACE INTO ${KEYVAL_TABLE} (key, value) VALUES (?, ?);`,
    [key, valueString],
  );
};

export const getSQLiteItem = async <T>(key: string): Promise<T | undefined> => {
  const db = await getDatabase();

  const { values } = await db.query(
    `SELECT value FROM ${KEYVAL_TABLE} WHERE key = ?`,
    [key],
  );

  const stringValue = values?.[0]?.value;

  if ([null, undefined].includes(stringValue)) {
    return undefined;
  }

  try {
    return JSON.parse(stringValue);
  } catch (err) {
    return stringValue;
  }
};

export const removeSQLiteItem = async (key: string): Promise<void> => {
  const db = await getDatabase();
  await db.query(`DELETE FROM ${KEYVAL_TABLE} WHERE key = ?;`, [key]);
};

export const clearSQLite = async (): Promise<void> => {
  const db = await getDatabase();
  await db.query(`DROP TABLE IF EXISTS ${KEYVAL_TABLE}`);
  await createSQLiteTable();
};
